import { normalizeChars, type RegisterTarget } from './subset';
import type { SubsetLoadOptions, SubsetPreparationOptions } from './types';

/**
 * 同时执行的字体预备批次数上限。
 *
 * 一个批次包含同一调度轮、注册目标和字体族的全部请求。
 * 上限 6 来自当前需求：它以固定吞吐换取首次渲染时可预测的下载与注册压力，
 * 暂不增加配置入口，避免在没有运行数据时引入无法验证的调优面。
 */
const CONCURRENCY = 6;

/**
 * 立即加载一个精确字体子集，不参与 preparation 排队。
 *
 * @param fontFamily - 字体族名称。
 * @param chars - 需要覆盖的完整字符集。
 * @param options - 字体注册目标。
 * @returns 可用字体族；加载后仍不可用时返回 null。
 */
type FontSubsetLoader = (fontFamily: string, chars: string, options: SubsetLoadOptions) => Promise<string | null>;

/**
 * 一次尚未收口的字体预备请求。
 *
 * 该记录只保存调度所需状态，不持有图层或字体 manager 数据。
 */
type Preparation = {
    /**
     * 本次请求需要准备的字体族，也是批次合并身份的一部分。
     */
    fontFamily: string;

    /**
     * 调用方声明需要完整覆盖的字符集。
     */
    chars: string;

    /**
     * 字体注册目标，用于隔离 engine 与 document 批次。
     */
    registerTo: RegisterTarget;

    /**
     * 请求进入队列时所属的调度轮，用于阻止跨轮合并。
     */
    round: number;

    /**
     * 调用方取消信号，仅允许取消尚未开始的请求。
     */
    signal?: AbortSignal;

    /**
     * 注册到取消信号的监听函数，用于请求收口时解除监听。
     */
    abort?: () => void;

    /**
     * 请求是否已经进入即时加载批次；开始后不再响应取消。
     */
    started: boolean;

    /**
     * 请求是否已经完成，用于防止取消、卸载和加载结果重复收口。
     */
    settled: boolean;

    /**
     * `schedule` 返回 Promise 的 resolve 函数。
     *
     * 批次加载成功时接收可用字体族；请求取消、服务卸载、字符为空或加载结果不可用时接收 null。
     */
    resolve: (subsetName: string | null) => void;

    /**
     * `schedule` 返回 Promise 的 reject 函数。
     *
     * loader 抛错时接收原始异常，使同批次中尚未收口的调用方 Promise 分别失败。
     */
    reject: (reason: unknown) => void;
};

/**
 * 调度明确声明为首次渲染预备的字体子集请求。
 *
 * 调度器只负责同轮合批、FIFO、固定并发和未启动请求取消；字体查询、复用、
 * 下载及注册策略全部由传入的即时加载函数负责。
 */
export class FontSubsetScheduler {
    /**
     * 等待获得并发槽位的字体预备请求。
     */
    private queue: Preparation[] = [];

    /**
     * 当前正在执行的字体预备批次数。
     */
    private activeBatchCount = 0;

    /**
     * 下一轮队列排空定时器；同一宏任务只安排一次。
     */
    private timer: ReturnType<typeof setTimeout> | null = null;

    /**
     * 当前收集轮次；宏任务开始排空时递增，隔离之后提交的请求。
     */
    private round = 0;

    /**
     * 调度器是否已经卸载。
     */
    private disposed = false;

    /**
     * 创建字体预备调度器。
     *
     * @param loader - 立即加载指定完整字符集的函数。
     */
    constructor(private readonly loader: FontSubsetLoader) {}

    /**
     * 启用调度器并允许接收新的字体预备请求。
     *
     * @returns 无返回值。
     */
    public mount(): void {
        this.disposed = false;
    }

    /**
     * 停止调度器，并让所有尚未开始的请求以不可用结果收口。
     *
     * 已经交给即时加载函数的批次不会被强制中断，避免改变底层字体加载生命周期。
     *
     * @returns 无返回值。
     */
    public unmount(): void {
        this.disposed = true;
        if (this.timer !== null) clearTimeout(this.timer);
        this.timer = null;

        this.queue.forEach((preparation) => this.resolve(preparation, null));
        this.queue = [];
    }

    /**
     * 提交一次允许排队削峰的字体子集请求。
     *
     * @param fontFamily - 字体族名称。
     * @param chars - 本次请求需要覆盖的完整字符集。
     * @param options - 注册目标及调用方取消信号。
     * @returns 当前请求批次得到的可用字体族；取消或不可用时返回 null。
     */
    public schedule(fontFamily: string, chars: string, options?: SubsetPreparationOptions): Promise<string | null> {
        // 卸载后或提交前已经取消的请求不能进入队列，避免访问已释放的字体服务。
        if (this.disposed || options?.signal?.aborted) return Promise.resolve(null);

        return new Promise<string | null>((resolve, reject) => {
            const preparation: Preparation = {
                fontFamily,
                chars,
                registerTo: options?.registerTo ?? 'engine',
                round: this.round,
                signal: options?.signal,
                started: false,
                settled: false,
                resolve,
                reject,
            };

            if (preparation.signal) {
                preparation.abort = () => {
                    // 取消只作用于尚未交给即时加载函数的请求；运行中批次沿用原有生命周期。
                    if (!preparation.started) this.resolve(preparation, null);
                };
                preparation.signal.addEventListener('abort', preparation.abort, { once: true });
            }

            this.queue.push(preparation);
            this.scheduleDrain();
        });
    }

    /**
     * 在下一宏任务安排一次队列排空，让同轮请求有机会合并。
     *
     * @returns 无返回值。
     */
    private scheduleDrain(): void {
        if (this.timer !== null) return;

        this.timer = setTimeout(() => {
            this.timer = null;
            // 排空开始即关闭当前收集轮，避免槽位不足时把后续宏任务的请求并入旧批次。
            this.round += 1;
            this.drain();
        }, 0);
    }

    /**
     * 按 FIFO 启动批次，直到并发槽位用满或队列为空。
     *
     * @returns 无返回值。
     */
    private drain(): void {
        while (!this.disposed && this.activeBatchCount < CONCURRENCY) {
            const first = this.queue.shift();
            if (!first) return;
            if (first.settled) continue;

            const batch = [first];
            this.queue = this.queue.filter((candidate) => {
                if (candidate.settled) return false;

                // 轮次、注册目标和字体族共同定义批次，避免跨轮或跨目标合并字体资源。
                if (
                    candidate.round !== first.round ||
                    candidate.registerTo !== first.registerTo ||
                    candidate.fontFamily !== first.fontFamily
                ) {
                    return true;
                }

                batch.push(candidate);
                return false;
            });
            batch.forEach((preparation) => {
                preparation.started = true;
            });

            this.activeBatchCount += 1;
            void this.runBatch(batch).finally(() => {
                // 所有终态都必须归还槽位，否则第七个及后续批次会永久停留在队列中。
                this.activeBatchCount -= 1;
                this.drain();
            });
        }
    }

    /**
     * 合并并执行一批同注册目标、同字体族的预备请求。
     *
     * @param preparations - 已获得并发槽位的请求集合。
     * @returns 批次内所有调用方完成收口后 resolve。
     */
    private async runBatch(preparations: Preparation[]): Promise<void> {
        const first = preparations[0];
        if (!first) return;

        const chars = normalizeChars(preparations.map((preparation) => preparation.chars).join(''));
        if (!chars) {
            preparations.forEach((preparation) => this.resolve(preparation, null));
            return;
        }

        try {
            const subsetName = await this.loader(first.fontFamily, chars, { registerTo: first.registerTo });
            preparations.forEach((preparation) => this.resolve(preparation, subsetName));
        } catch (error) {
            preparations.forEach((preparation) => this.reject(preparation, error));
        }
    }

    /**
     * 以成功或不可用结果完成一次请求，并移除取消监听。
     *
     * @param preparation - 需要完成的请求。
     * @param subsetName - 可用字体族；取消或不可用时为 null。
     * @returns 无返回值。
     */
    private resolve(preparation: Preparation, subsetName: string | null): void {
        if (preparation.settled) return;

        preparation.settled = true;
        if (preparation.signal && preparation.abort) {
            preparation.signal.removeEventListener('abort', preparation.abort);
        }
        preparation.resolve(subsetName);
    }

    /**
     * 以加载异常结束一次请求，并移除取消监听。
     *
     * @param preparation - 需要结束的请求。
     * @param error - 即时加载函数抛出的异常。
     * @returns 无返回值。
     */
    private reject(preparation: Preparation, error: unknown): void {
        if (preparation.settled) return;

        preparation.settled = true;
        if (preparation.signal && preparation.abort) {
            preparation.signal.removeEventListener('abort', preparation.abort);
        }
        preparation.reject(error);
    }
}
